import { HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import {
  CheckmarkCircle01Icon,
  SquareIcon,
  CancelCircleIcon,
} from '@terra/ui-icons';
import { Button } from '../../../components/styled/button';
import { Text } from '../../../components/styled/text';
import { MessageSkeletonCard } from './MessageSkeletonCard';
import { MarkdownPreview } from '../../markdown-preview';
import type {
  PromptOption,
  StopConfirmationMessage,
} from '@terra/shared-dto-chat-types';

export function StopConfirmationPromptCard({
  dataMessage,
  selectedValue,
  onDataMessageEvent,
}: {
  selectedValue?: string;
  dataMessage?: StopConfirmationMessage;
  onDataMessageEvent?: (event: {
    dataMessage: StopConfirmationMessage;
    selectedOption: PromptOption;
  }) => void;
}) {
  const tGuardrail = useTranslations(
    'designSystem.chat.guardrailConfirmationPromptCard',
  );
  const tFindings = useTranslations(
    'designSystem.findings.structuredPocEditor',
  );
  const stopConfirmationPrompt = dataMessage?.stopConfirmationPrompt;

  if (!stopConfirmationPrompt) {
    return null;
  }

  const selectedOption = selectedValue
    ? stopConfirmationPrompt.options.find(
        (option) => option.value === selectedValue,
      )
    : undefined;
  const isApproved = selectedValue?.toLowerCase() === 'approve';
  const isRejected = selectedValue?.toLowerCase() === 'reject';

  return (
    <MessageSkeletonCard
      type="toolInvocation"
      header={
        <HStack gap={3} justifyContent="space-between" width="100%">
          <HStack gap={3}>
            {isApproved ? (
              <CheckmarkCircle01Icon
                size="20px"
                color="var(--colors-green-11)"
              />
            ) : isRejected ? (
              <CancelCircleIcon size="20px" color="var(--colors-red-11)" />
            ) : (
              <VStack
                alignItems="center"
                justifyContent="center"
                backgroundColor="terra.9"
                borderRadius="sm"
                padding={1}
                color="white"
              >
                <SquareIcon size="12px" fill="currentColor" />
              </VStack>
            )}
            <Text
              fontSize="sm"
              fontWeight="600"
              color={selectedValue ? 'fg.muted' : 'fg.default'}
            >
              {stopConfirmationPrompt.title}
            </Text>
          </HStack>
        </HStack>
      }
      footer={
        <HStack gap={2} flexDirection="row-reverse">
          {selectedValue ? (
            <Button size="sm" variant="outline" disabled>
              {selectedOption?.label || selectedValue}
            </Button>
          ) : (
            stopConfirmationPrompt.options.map((option, index) => {
              const isApproveButton = option.value.toLowerCase() === 'approve';

              return (
                <Button
                  size="sm"
                  key={index}
                  variant={isApproveButton ? 'solid' : 'outline'}
                  onClick={() =>
                    onDataMessageEvent?.({
                      dataMessage,
                      selectedOption: option,
                    })
                  }
                >
                  {option.label}
                </Button>
              );
            })
          )}
        </HStack>
      }
    >
      <VStack alignItems="start" gap={4}>
        <VStack alignItems="start" gap={1} width="100%">
          <MarkdownPreview source={stopConfirmationPrompt.description} />
        </VStack>

        {stopConfirmationPrompt.stopReason && (
          <VStack alignItems="start" gap={2} width="100%">
            <Text fontSize="xs" fontWeight="600" color="fg.muted">
              {tGuardrail('reason')}
            </Text>
            <VStack
              alignItems="start"
              gap={2}
              width="100%"
              backgroundColor="bg.subtle"
              borderRadius="md"
              padding={3}
              borderWidth="1px"
              borderColor="border.default"
            >
              <Text fontSize="sm" color="fg.default">
                {stopConfirmationPrompt.stopReason}
              </Text>
            </VStack>
          </VStack>
        )}

        {stopConfirmationPrompt.evidence && (
          <VStack alignItems="start" gap={2} width="100%">
            <Text fontSize="xs" fontWeight="600" color="fg.muted">
              {tFindings('evidence')}
            </Text>
            <VStack
              alignItems="start"
              gap={2}
              width="100%"
              backgroundColor="gray.1"
              borderRadius="md"
              padding={3}
              borderWidth="1px"
              borderColor="gray.5"
            >
              <MarkdownPreview source={stopConfirmationPrompt.evidence} />
            </VStack>
          </VStack>
        )}
      </VStack>
    </MessageSkeletonCard>
  );
}
